import { useEffect, useMemo } from "react";

import { useUpsellStore } from "../store/useUpsellStore";

const MODAL_PROGRESS_ANIMATION_DELAY = 300;

type UseUpsellUiStateArgs = {
  hasCountdown: boolean;
};

const formatCountdown = (timeLeft: number) => {
  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor((timeLeft % 3600) / 60);
  const seconds = timeLeft % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

export const useUpsellUiState = ({ hasCountdown }: UseUpsellUiStateArgs) => {
  const timeLeft = useUpsellStore((state) => state.timeLeft);
  const isModalOpen = useUpsellStore((state) => state.isModalOpen);
  const setModalOpen = useUpsellStore((state) => state.setModalOpen);
  const tickCountdown = useUpsellStore((state) => state.tickCountdown);
  const setModalProgressAnimated = useUpsellStore(
    (state) => state.setModalProgressAnimated,
  );

  useEffect(() => {
    if (!hasCountdown) return;

    const timer = setInterval(() => {
      tickCountdown();
    }, 1000);

    return () => clearInterval(timer);
  }, [hasCountdown, tickCountdown]);

  useEffect(() => {
    if (!isModalOpen) {
      setModalProgressAnimated(false);
      return;
    }

    const timer = window.setTimeout(
      () => setModalProgressAnimated(true),
      MODAL_PROGRESS_ANIMATION_DELAY,
    );

    return () => {
      window.clearTimeout(timer);
    };
  }, [isModalOpen, setModalProgressAnimated]);

  useEffect(() => {
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isModalOpen) {
        setModalOpen(false);
      }
    };

    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [isModalOpen, setModalOpen]);

  const countdownText = useMemo(() => formatCountdown(timeLeft), [timeLeft]);
  const isCountdownExpired = timeLeft <= 0;

  return {
    countdownText,
    isCountdownExpired,
  };
};
